import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation, useNavigate, useRoutes, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';

import { loginRequest } from '@core/auth/msal.config';
import { can, hasFeature, isFlagOn, useEntitlements, useFlag } from '@core/access';
import { NAV_DATA } from '@core/nav-data';
import { useAppraisalStore } from '@core/state/appraisal.store';
import { useSectionStore } from '@core/state/section.store';
import { useUserStore } from '@core/state/user.store';
import { clearUserCache } from '@core/state/user-cache';
import type { NavItem } from '@shared/models/nav-item.model';
import { storage, useLocalStorage } from '@shared/hooks/useLocalStorage';
import { useNavigateTo, useTrackHistory } from '@shared/hooks/useReturnTo';
import { useIsCompact, useIsHandset } from '@shared/hooks/useMediaQuery';
import { Sidenav, SidenavContainer, SidenavContent } from '@ui/Sidenav';
import { Menu, MenuItem } from '@ui/Menu';
import { Icon } from '@ui/Icon';
import { Tooltip } from '@ui/Tooltip';
import { Loading } from '@ui/Loading';
import { ThemeSwitch } from '@ui/ThemeSwitch';
import { ToolBar } from '@layout/ToolBar';
import { SidebarNav } from '@layout/SidebarNav';
import { AssistantContainer } from '@features/assistant/assistant-container/AssistantContainer';
import { activityRouteOf, buildActivityContext, getInitials, sectionRoute } from '@layout/activity';
import { ASSIGNMENTS } from '@features/my-assignments/my-assignments.data';
import { SIGNED_OUT_MARKER } from '@app/RequireAuth';
import { BootScreen } from '@app/BootScreen';
import { routes } from '@app/routes';

import './App.scss';

const RECENT_RECORDS_KEY = 'recentRecords';
const RECENT_RECORDS_LIMIT = 10;

/**
 * The application shell.
 *
 * It owns the three things every screen sits inside - the toolbar, the left
 * nav rail and the right-hand assistant drawer - plus the routed view between
 * them. Store subscriptions, route changes and the breakpoint queries all
 * arrive as hooks and effects:
 *
 *   useAppraisalStore(s => s.rowData)  the record the shell reads for context
 *   useSectionStore(...)               the section the nav highlights
 *   useEffect on `pathname`            per-navigation shell bookkeeping
 *   useIsCompact() / useIsHandset()    drawer mode and rail behaviour
 *   `opened` state on <Sidenav>        drawer open/close
 */
export function App() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCompact = useIsCompact();
  const isHandset = useIsHandset();

  const element = useRoutes(routes);

  // Lets return links step back to the screen the user came from (useReturnTo).
  useTrackHistory();
  const navigateTo = useNavigateTo();

  /* ---------------------------------------------------------------- layout -- */

  // Desktop-only: as an overlay the rail is either on screen at full width or
  // not at all, so there is no collapsed state to toggle.
  const [collapsed, setCollapsed] = useLocalStorage('leftNavCollapsed', false);
  const [navOpen, setNavOpen] = useState(!isCompact);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const assistantEnabled = useFlag('assistant');

  // Docked drawers are part of the layout and must stay put; overlay drawers
  // follow the viewport.
  useEffect(() => {
    setNavOpen(!isCompact);

    if (!isCompact) {
      setAssistantOpen(false);
    }
  }, [isCompact]);

  /* --------------------------------------------------------------- user/nav -- */

  const userData = useUserStore((state) => state.userData);
  const entitlements = useEntitlements();

  /*
   * The nav the signed-in user actually holds.
   *
   * One rule, applied to every row and to every child: an item needing a
   * feature, a capability or a flag appears only if all of them hold - a
   * feature counts only while its flag is on - and an item needing none is
   * for everybody. The shell used to carry two special
   * cases instead - one comparing titles against `'Ratings'`, one calling into
   * the Singapore module's own region gate - which is why adding a product
   * surface meant editing this function.
   *
   * A branch whose children are all filtered away keeps its own row when it
   * has a route of its own, and disappears when it does not: a parent that
   * exists only to group links is nothing without them.
   */
  const navData = useMemo<NavItem[]>(() => {
    if (entitlements.pending) {
      return [];
    }

    const permitted = (item: NavItem): boolean =>
      (!item.feature || hasFeature(entitlements, item.feature)) &&
      (!item.capability || can(entitlements, item.capability)) &&
      (!item.flag || isFlagOn(entitlements, item.flag));

    const prune = (items: NavItem[]): NavItem[] =>
      items.filter(permitted).flatMap((item) => {
        if (!item.children) {
          return [item];
        }

        const children = prune(item.children);

        if (children.length === 0) {
          return item.route ? [{ ...item, children: undefined }] : [];
        }

        return [{ ...item, children }];
      });

    return prune(NAV_DATA);
  }, [entitlements]);

  /*
   * All opportunities opens empty and answers a search in under a second,
   * so its records are fetched here, while the browser is idle after
   * sign-in - not when somebody first searches.
   */
  const queryClient = useQueryClient();
  const canViewOpportunities = !entitlements.pending && can(entitlements, 'opportunities.view');

  useEffect(() => {
    if (!canViewOpportunities) {
      return;
    }

    const warm = () => void import('@features/crm/warm-up').then((module) => module.warmOpportunities(queryClient));

    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(warm, { timeout: 2000 });
      return () => window.cancelIdleCallback(handle);
    }

    // Safari has no idle callback; a short delay keeps the first paint first.
    const handle = setTimeout(warm, 300);
    return () => clearTimeout(handle);
  }, [canViewOpportunities, queryClient]);

  /* --------------------------------------------------------------- activity -- */

  const rowData = useAppraisalStore((state) => state.rowData);
  const setRowData = useAppraisalStore((state) => state.setRowData);

  const storedSubsection = useSectionStore((state) => state.selectedSubSection);
  const setSelectedSubSection = useSectionStore((state) => state.setSelectedSubSection);
  const clearSelectedSubSection = useSectionStore((state) => state.clearSelectedSubSection);

  // The section and activity the URL is on. Read from the route rather than
  // the store, so Back and Forward move the sidebar with the page - the store
  // only changes when something writes it, and Back writes nothing.
  const activityRoute = useMemo(() => activityRouteOf(pathname), [pathname]);
  const selectedSubsection = activityRoute?.section ?? storedSubsection ?? 'Subject Property';

  const [isDynamicSectionExpanded, setDynamicSectionExpanded] = useState(true);

  /*
   * The recently opened records, by activity number.
   *
   * Cached so a redirect sign-in - a full page reload - comes back to the same
   * activity, and so Back to an activity left through the nav finds its card
   * again. Several are kept rather than the last one: Back can step over more
   * than one record, and only the subject page re-fetches its own.
   */
  useEffect(() => {
    const activityNumber = buildActivityContext(rowData).activityNumber;

    if (!activityNumber) {
      return;
    }

    const recent = storage.getJson<Record<string, unknown>>(RECENT_RECORDS_KEY) ?? {};
    delete recent[activityNumber];
    const kept = Object.entries({ [activityNumber]: rowData, ...recent }).slice(0, RECENT_RECORDS_LIMIT);

    storage.setJson(RECENT_RECORDS_KEY, Object.fromEntries(kept));
  }, [rowData]);

  /*
   * Show the record the URL names: on a direct landing, and when Back or
   * Forward arrives at a different activity than the one in the sidebar.
   *
   * Runs on route changes only. Leaving through a nav row clears the record
   * before the route has moved, and re-running on that would put it straight
   * back.
   */
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;

  useEffect(() => {
    if (!activityRoute) {
      return;
    }

    if (buildActivityContext(rowDataRef.current).activityNumber === activityRoute.activityNumber) {
      return;
    }

    const cached = storage.getJson<Record<string, unknown>>(RECENT_RECORDS_KEY)?.[activityRoute.activityNumber];

    if (cached) {
      setRowData(cached);
      setDynamicSectionExpanded(true);
    }
  }, [activityRoute, setRowData]);

  // Keep the store - and with it a reload - in step with the section on screen.
  useEffect(() => {
    if (activityRoute && activityRoute.section !== storedSubsection) {
      setSelectedSubSection(activityRoute.section);
    }
  }, [activityRoute, storedSubsection, setSelectedSubSection]);

  const activity = useMemo(() => buildActivityContext(rowData), [rowData]);

  const selectSection = useCallback(
    (section: string) => {
      setSelectedSubSection(section);
      navigateTo(sectionRoute(section, activity.activityNumber || rowData?.activityNumber || ''));
    },
    [activity.activityNumber, navigateTo, rowData, setSelectedSubSection]
  );

  /** The card itself is the shortcut back to the record: it opens the subject
      detail and reveals the sub-sections, rather than only toggling them. */
  const openSubjectDetail = useCallback(() => {
    if (!rowData) {
      return;
    }

    setDynamicSectionExpanded(true);
    selectSection(activity.recordSections[0] ?? 'Subject Property');
  }, [rowData, activity.recordSections, selectSection]);

  // The card leaves the sidebar, but the cache stays so Back can bring it back.
  const clearSelectedRecord = useCallback(() => {
    setRowData(null);
    clearSelectedSubSection();
  }, [setRowData, clearSelectedSubSection]);

  /** True while any page of the open activity is showing, so the card can read
      as the current location even when a sub-section is the active row. */
  const isActivityRouteActive = pathname.includes('activity-details');

  /* ----------------------------------------------------------------- search -- */

  /*
   * The search is in the bar on every screen but Home and Search, which carry
   * their own box. On the assignment screens it filters in place through
   * `?q=`; anywhere else it holds a draft and Enter opens the results page.
   * AI Mode beside it asks the term as a question.
   */
  const onAssignments = pathname.startsWith('/assignments');
  const ownsSearch = pathname === '/' || pathname === '/home' || pathname === '/search';
  const [draftSearch, setDraftSearch] = useState('');
  const toolbarSearchTerm = onAssignments ? searchParams.get('q') ?? '' : draftSearch;

  const submitToolbarSearch = useCallback(
    (value: string) => {
      if (onAssignments || !value.trim()) {
        return;
      }

      setDraftSearch('');
      navigate(`/search?q=${encodeURIComponent(value.trim())}`);
    },
    [onAssignments, navigate]
  );

  const openAiMode = useCallback(
    (value: string) => {
      setDraftSearch('');
      navigate(value.trim() ? `/search?tab=ai&q=${encodeURIComponent(value.trim())}` : '/search?tab=ai');
    },
    [navigate]
  );

  const assignmentSearchSuggestions = useMemo(() => {
    const suggestions = new Set<string>();

    for (const assignment of ASSIGNMENTS) {
      suggestions.add(assignment.id);
      suggestions.add(assignment.client);
      suggestions.add(assignment.property);
      suggestions.add(assignment.appraiser);
      suggestions.add(assignment.type);
    }

    return Array.from(suggestions).sort((left, right) => left.localeCompare(right));
  }, []);

  const updateToolbarSearch = useCallback(
    (value: string) => {
      if (!onAssignments) {
        setDraftSearch(value);
        return;
      }

      const query = value.trim();

      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (query) {
            next.set('q', query);
          } else {
            next.delete('q');
          }

          return next;
        },
        { replace: true }
      );
    },
    [onAssignments, setSearchParams]
  );

  /* ------------------------------------------------------------- navigation -- */

  /*
   * An overlay drawer that survives a navigation leaves the user looking at
   * the nav they just used, with the page they asked for hidden behind it.
   * Docked, the drawers are part of the layout and must stay put.
   */
  useEffect(() => {
    if (!isCompact) {
      return;
    }

    setNavOpen(false);
    setAssistantOpen(false);
  }, [pathname, isCompact]);

  const toggleSidenav = useCallback(() => {
    // As an overlay the rail has no collapsed state to toggle - the button is
    // an open/close, which is what a hamburger means on a small screen.
    if (isCompact) {
      setNavOpen((open) => !open);
      return;
    }

    setCollapsed((value) => !value);
  }, [isCompact, setCollapsed]);

  const loginRedirect = useCallback(() => {
    void instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(() => {
    storage.remove('selectedSubsection');
    storage.remove('selectedRecord');
    storage.remove(RECENT_RECORDS_KEY);
    clearUserCache();

    // Tells the auth guard this tab left on purpose. The guard signs in
    // automatically on finding no account, so without this the logout redirect
    // would return to a shell that immediately sent the user back to Entra and
    // signed them straight back in.
    try {
      sessionStorage.setItem(SIGNED_OUT_MARKER, 'true');
    } catch {
      /* the guard fails closed to the manual screen when storage is unavailable */
    }

    void instance.logoutRedirect();
  }, [instance]);

  /* ------------------------------------------------------------------ render -- */

  const railCollapsed = collapsed && !isCompact;

  /* No signed-out branch here any more. It used to be an early return of the
     sign-in screen, which is what made SSO wait on a button click; `RequireAuth`
     in main.tsx now owns that state and starts the redirect on its own, so this
     component only ever renders for a signed-in user. */
  return (
    <div className="layout-container">
      {/* A first visit has no cached user to draw the nav from, so the sign-in
          steps stay up until the toolbar below has resolved one. A returning
          user never sees this: the shell renders from cache (user-cache.ts). */}
      {entitlements.pending && <BootScreen />}

      <ToolBar
        onToggleSidenav={toggleSidenav}
        onToggleRightSidenav={assistantEnabled ? () => setAssistantOpen((open) => !open) : undefined}
        onLoginRedirect={loginRedirect}
        showSearch={!ownsSearch}
        onAiMode={openAiMode}
        searchTerm={toolbarSearchTerm}
        searchSuggestions={assignmentSearchSuggestions}
        onSearchChanged={updateToolbarSearch}
        onSearchSubmit={submitToolbarSearch}
      />

      <SidenavContainer
        id="leftSideNavContainer"
        className={[
          'left-sidenav-container',
          isCompact ? 'nav-compact' : null,
          !collapsed && !isCompact ? 'nav-expanded' : null,
          railCollapsed ? 'nav-rail' : null
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* fixedTopGap matches the 40px toolbar - it read 56px, which left the
            drawer starting 16px below the bar with the page showing through. */}
        <Sidenav
          className={['left-sidenav', railCollapsed ? 'rail' : null].filter(Boolean).join(' ')}
          mode={isCompact ? 'over' : 'side'}
          opened={isCompact ? navOpen : true}
          onClose={() => setNavOpen(false)}
          fixedInViewport={isCompact}
          fixedTopGap={40}
        >
          <div className="sidebar-shell">
            <div className="sidebar-body">
              <div className="sidebar-scroll">
                <SidebarNav items={navData} collapsed={collapsed} onLeafClick={clearSelectedRecord} />

                {rowData && <div className="sidebar-divider" />}

                {rowData && (
                  <section
                    className={['activity', isDynamicSectionExpanded ? 'is-open' : null]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="sidebar-section__label">Activity</div>

                    <div
                      className={[
                        'activity-card',
                        isDynamicSectionExpanded ? 'is-open' : null,
                        isActivityRouteActive ? 'is-active' : null
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <Tooltip
                        message={collapsed ? activity.tooltip : 'Open subject detail'}
                        position={collapsed ? 'right' : 'below'}
                        showDelay={350}
                      >
                        <button type="button" className="activity-card__open" onClick={openSubjectDetail}>
                          <span className="activity-card__glyph">
                            <Icon>apartment</Icon>
                          </span>
                          {/* Property name leads: it is the human handle for the
                              record. The number stays as the caption under it -
                              still always visible, but no longer the loudest
                              thing in the sidebar. */}
                          <span
                            className={[
                              'activity-card__text',
                              activity.propertyName ? null : 'is-solo'
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {activity.propertyName && (
                              <span className="activity-card__property" title={activity.propertyName}>
                                {activity.propertyName}
                              </span>
                            )}
                            <span className="activity-card__number" title={activity.activityNumber}>
                              {activity.activityNumber}
                            </span>
                          </span>
                        </button>
                      </Tooltip>

                      {!collapsed && (
                        <button
                          type="button"
                          className="activity-card__toggle"
                          onClick={() => setDynamicSectionExpanded((value) => !value)}
                          aria-expanded={isDynamicSectionExpanded}
                          aria-label={`${isDynamicSectionExpanded ? 'Collapse' : 'Expand'} activity sections`}
                        >
                          <Icon>expand_more</Icon>
                        </button>
                      )}
                    </div>

                    {!collapsed && (activity.statusLabel || activity.location) && (
                      <div className="activity-meta">
                        {activity.statusLabel && (
                          <span className="activity-chip" data-tone={activity.statusTone}>
                            <span className="activity-chip__dot" />
                            {activity.statusLabel}
                          </span>
                        )}
                        {activity.location && (
                          <span className="activity-meta__location" title={activity.location}>
                            {activity.location}
                          </span>
                        )}
                      </div>
                    )}

                    <div
                      className={['activity-sections', isDynamicSectionExpanded ? 'is-open' : null]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden={!isDynamicSectionExpanded}
                      inert={!isDynamicSectionExpanded}
                    >
                      <div className="activity-sections__inner">
                        {activity.sections.map((section, index) => (
                          <button
                            key={section.key}
                            type="button"
                            className={[
                              'activity-link',
                              selectedSubsection === section.key ? 'is-active' : null
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            style={{ '--row': index } as CSSProperties}
                            onClick={() => selectSection(section.key)}
                          >
                            <Icon className="activity-link__icon">{section.icon}</Icon>
                            <span className="activity-link__label">{section.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </section>
                )}
              </div>
            </div>

            {userData && (
              <Menu
                xPosition="after"
                yPosition="above"
                panelClass="profile-menu-panel"
                trigger={({ onClick }) => (
                  <button
                    type="button"
                    className="sidebar-footer"
                    aria-label="Profile menu"
                    onClick={onClick}
                  >
                    <span className="sidebar-footer__avatar">{getInitials(userData.displayName)}</span>
                    {!collapsed && (
                      <span className="sidebar-footer__who">
                        <span className="sidebar-footer__name">{userData.displayName}</span>
                        <span className="sidebar-footer__role">{userData.currentRole}</span>
                      </span>
                    )}
                    {!collapsed && <Icon className="sidebar-footer__more">more_horiz</Icon>}
                  </button>
                )}
              >
                {/* Clicking your own name is inert rather than dismissing the menu. */}
                <div className="profile-menu-header" onClick={(event) => event.stopPropagation()}>
                  <span className="profile-menu-header__avatar">{getInitials(userData.displayName)}</span>
                  <span className="profile-menu-header__who">
                    <span className="profile-menu-header__name">{userData.displayName}</span>
                    <span className="profile-menu-header__email">{userData.email}</span>
                    <span className="profile-menu-header__role">{userData.currentRole}</span>
                  </span>
                </div>

                {/* Inert like the header: picking a theme should not close the
                    menu, so the change can be seen and undone in place. */}
                <div className="profile-menu-section-label">Appearance</div>
                <div className="profile-menu-theme" onClick={(event) => event.stopPropagation()}>
                  <ThemeSwitch />
                </div>
                <div className="profile-menu-divider" />

                <MenuItem className="profile-menu-row">
                  <span className="profile-menu-item">
                    <i className="fa fa-gear profile-menu-item__icon" />
                    <span className="profile-menu-item__label">Settings</span>
                  </span>
                </MenuItem>
                <MenuItem className="profile-menu-row" onClick={logout}>
                  <span className="profile-menu-item">
                    <i className="fa fa-arrow-right-from-bracket profile-menu-item__icon" />
                    <span className="profile-menu-item__label">Sign out</span>
                  </span>
                </MenuItem>

                <div className="profile-menu-divider" />
                <div className="profile-menu-section-label">Help</div>

                <MenuItem className="profile-menu-row">
                  <span className="profile-menu-item">
                    <i className="fa fa-file-lines profile-menu-item__icon" />
                    <span className="profile-menu-item__label">Documentation</span>
                    <i className="fa fa-arrow-up-right-from-square profile-menu-item__external" />
                  </span>
                </MenuItem>
                <MenuItem className="profile-menu-row">
                  <span className="profile-menu-item">
                    <i className="fa fa-graduation-cap profile-menu-item__icon" />
                    <span className="profile-menu-item__label">Training</span>
                    <i className="fa fa-arrow-up-right-from-square profile-menu-item__external" />
                  </span>
                </MenuItem>
                <MenuItem className="profile-menu-row">
                  <span className="profile-menu-item">
                    <i className="fa fa-headset profile-menu-item__icon" />
                    <span className="profile-menu-item__label">Support</span>
                  </span>
                </MenuItem>
              </Menu>
            )}
          </div>
        </Sidenav>

        {/* The assistant docks as a column only when there is room for one beside
            the content. Below that it overlays, and on a handset it takes the
            whole width - a 438px panel with 40px of page peeking past it is
            neither a panel nor a page. */}
        {assistantEnabled && (
          <Sidenav
            className={['universal-chat-sidenav', isHandset ? 'is-fullbleed' : null]
              .filter(Boolean)
              .join(' ')}
            position="end"
            mode={isCompact ? 'over' : 'side'}
            opened={assistantOpen}
            onClose={() => setAssistantOpen(false)}
            fixedInViewport={isCompact}
            fixedTopGap={40}
          >
            <AssistantContainer onClosePane={() => setAssistantOpen(false)} />
          </Sidenav>
        )}

        <SidenavContent>
          <main className="main">
            <div className="content-container">
              <Suspense fallback={<RouteFallback />}>{element}</Suspense>
            </div>
          </main>
        </SidenavContent>
      </SidenavContainer>
    </div>
  );
}

/**
 * Shown while a lazily loaded screen's chunk is in flight.
 *
 * `page` centres it in the routed area. It used to be a bare spinner in a
 * `.route-loading` div that no stylesheet ever matched, so the app's most
 * frequently seen loading state rendered in the top-left corner of an
 * otherwise empty screen.
 */
function RouteFallback() {
  return <Loading variant="page" label="Loading" />;
}

export default App;
